import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, PageHeader, Pill } from "@/components/common";
import {
  BarChart, Bar, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Analytics() {
  const [sum, setSum] = useState(null);
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get("/analytics/summary").then(({ data }) => setSum(data)).catch(() => {});
    api.get("/analytics/top-products").then(({ data }) => setTop(data)).catch(() => {});
  }, []);

  const series = (sum?.series || []).map((m) => ({
    date: m.date.slice(5),
    revenue: m.revenue,
    pod_cost: m.pod_cost,
    ad_spend: m.ad_spend,
  }));

  const breakdown = sum ? [
    { name: "POD Cost", value: sum.pod_cost },
    { name: "Ad Spend", value: sum.ad_spend },
    { name: "Fees", value: sum.fees },
    { name: "Net Profit", value: sum.profit },
  ] : [];

  return (
    <>
      <PageHeader title="Profit & Performance" subtitle="Real profit after POD cost, ad spend and platform fees — plus auto-optimization signals." testid="analytics-header">
        <Pill tone="emerald">{sum ? `${sum.roas}x ROAS` : "—"}</Pill>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Revenue", money(sum?.revenue), "emerald"], ["POD Cost", money(sum?.pod_cost), "slate"], ["Ad Spend", money(sum?.ad_spend), "amber"], ["Net Profit", money(sum?.profit), "emerald"]].map(([l, v, t]) => (
          <Card key={l} className="p-5" data-testid={`analytics-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs text-slate-400 font-mono uppercase tracking-wider">{l}</div>
            <div className={`mt-2 text-2xl font-head font-bold ${t === "emerald" ? "text-emerald-400" : t === "amber" ? "text-amber-300" : "text-slate-100"}`}>{v}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2" data-testid="revenue-cost-chart">
          <h3 className="font-head text-lg font-medium text-slate-200 mb-4">Revenue vs Cost (30d)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                  <linearGradient id="ad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#12161F", border: "1px solid #1e293b", borderRadius: 8, color: "#e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#rev)" />
                <Area type="monotone" dataKey="ad_spend" stroke="#f59e0b" strokeWidth={2} fill="url(#ad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6" data-testid="breakdown-chart">
          <h3 className="font-head text-lg font-medium text-slate-200 mb-4">Where revenue goes</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breakdown} layout="vertical" margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={80} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#12161F", border: "1px solid #1e293b", borderRadius: 8, color: "#e2e8f0" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-6" data-testid="top-products-table">
        <h3 className="font-head text-lg font-medium text-slate-200 mb-4">Top products & auto-optimization</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 font-mono uppercase tracking-wider border-b border-slate-800">
                <th className="py-2 pr-4">Product</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Units</th>
                <th className="py-2 pr-4">Revenue</th><th className="py-2 pr-4">Margin</th><th className="py-2 pr-4">Signal</th>
              </tr>
            </thead>
            <tbody>
              {top.map((p, i) => {
                const winner = p.revenue > 1500;
                return (
                  <tr key={i} className="border-b border-slate-800/60">
                    <td className="py-3 pr-4 text-slate-200 max-w-xs truncate">{p.title}</td>
                    <td className="py-3 pr-4 text-slate-400 capitalize">{p.product_type}</td>
                    <td className="py-3 pr-4 text-slate-300">{p.units}</td>
                    <td className="py-3 pr-4 text-emerald-400 font-medium">{money(p.revenue)}</td>
                    <td className="py-3 pr-4 text-slate-300">{p.margin_pct}%</td>
                    <td className="py-3 pr-4">
                      <Pill tone={winner ? "emerald" : "amber"}>{winner ? "Scale ads" : "Watch / cut"}</Pill>
                    </td>
                  </tr>
                );
              })}
              {top.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">No product data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
